import { length, map, groupBy } from 'ramda';
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';

import { Icon } from '@ui/icons/Icon.tsx';
import { withFocusTrap } from '@ui/modals/FocusTrap.tsx';

import useComponentFocused from '../../../../src/hooks/useComponentFocused.tsx';
import { useCSSColors } from '../../../../src/hooks/useCSSColors.ts';
import {
  MapNavigatorMobile,
  MapNavigatorMobileWrapper,
  BuildingWrapperMobile,
  BuildingDropdownMobile,
  LevelDropdownMobile,
  Submenu,
  DragCloseButton,
} from './styles.ts';

const OpenedMapLevelSelectorMobile = ({ widgetState, bus }) => {
  const colors = useCSSColors();
  const { isComponentFocused, setIsComponentFocused } = useComponentFocused(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [state, setState] = useState(widgetState.getState());
  // Roving tabindex: currently focused tree item id (building-<id> | level-<id>)
  const [focusedItemId, setFocusedItemId] = useState(null);
  const itemRefs = useRef({});

  useEffect(() => widgetState.addCallback(setState), [widgetState]);

  useEffect(() => {
    // if the outside click was detected, but menu was open, hide it
    if (!isComponentFocused && isMenuOpen) {
      closeMenu();
    }
  }, [isComponentFocused, isMenuOpen]);

  useEffect(() => {
    if (state.isMenuOpen) {
      setIsMenuOpen(true);
      setIsComponentFocused(true);
    } else {
      setIsMenuOpen(false);
    }
  }, [state.isMenuOpen, setIsComponentFocused]);

  // Build a flat list of visible tree items for keyboard navigation
  const visibleTreeItems = useMemo(() => {
    const items = [];
    if (!state.buildings) {
      return items;
    }
    state.buildings.forEach(b => {
      items.push({ type: 'building', id: b.id, building: b });
      if (state.selectedBuilding && state.selectedBuilding.id === b.id) {
        (b.levels || []).forEach(l => {
          if (l) {
            items.push({ type: 'level', id: l.id, level: l, parentId: b.id });
          }
        });
      }
    });
    return items;
  }, [state.buildings, state.selectedBuilding]);

  const firstItemId = visibleTreeItems.length
    ? visibleTreeItems[0].type === 'building'
      ? `building-${visibleTreeItems[0].id}`
      : `level-${visibleTreeItems[0].id}`
    : null;
  const lastItemId = visibleTreeItems.length
    ? (() => {
        const last = visibleTreeItems[visibleTreeItems.length - 1];
        return last.type === 'building' ? `building-${last.id}` : `level-${last.id}`;
      })()
    : null;

  const getIndex = useCallback(
    id =>
      visibleTreeItems.findIndex(i => {
        if (id.startsWith('building-')) {
          return i.type === 'building' && `building-${i.id}` === id;
        }
        if (id.startsWith('level-')) {
          return i.type === 'level' && `level-${i.id}` === id;
        }
        return false;
      }),
    [visibleTreeItems],
  );

  // Initialize or adjust focused item when structure/selection changes
  useEffect(() => {
    if (!visibleTreeItems.length) {
      return;
    }
    if (state.selectedLevel) {
      const target = `level-${state.selectedLevel.id}`;
      setFocusedItemId(prev => prev || target);
    } else if (state.selectedBuilding) {
      const target = `building-${state.selectedBuilding.id}`;
      setFocusedItemId(prev => prev || target);
    } else {
      setFocusedItemId(prev => prev || firstItemId);
    }
  }, [visibleTreeItems, state.selectedBuilding, state.selectedLevel, firstItemId]);

  // Move DOM focus to the new focused element
  useEffect(() => {
    if (focusedItemId && itemRefs.current[focusedItemId]) {
      itemRefs.current[focusedItemId].focus();
    }
  }, [focusedItemId, visibleTreeItems]);

  const focusByDelta = delta => {
    if (!focusedItemId) {
      return;
    }
    const idx = getIndex(focusedItemId);
    if (idx === -1) {
      return;
    }
    const next = visibleTreeItems[idx + delta];
    if (!next) {
      return;
    }
    setFocusedItemId(next.type === 'building' ? `building-${next.id}` : `level-${next.id}`);
  };

  const focusFirstChild = buildingId => {
    const firstChild = visibleTreeItems.find(i => i.type === 'level' && i.parentId === buildingId);
    if (firstChild) {
      setFocusedItemId(`level-${firstChild.id}`);
    }
  };

  const focusParent = levelItem => {
    if (!levelItem.parentId) {
      return;
    }
    setFocusedItemId(`building-${levelItem.parentId}`);
  };

  const isBuildingExpanded = building => state.selectedBuilding && state.selectedBuilding.id === building.id;
  const expandBuilding = building => {
    if (!isBuildingExpanded(building)) {
      bus.send('mapLevelSelector/selectBuilding', { id: building.id, userInitiated: true });
    }
  };
  const collapseBuilding = building => {
    if (isBuildingExpanded(building)) {
      // Assumes handler accepts null to collapse; if not, adjust bus event accordingly.
      bus.send('mapLevelSelector/selectBuilding', { id: null, userInitiated: true });
    }
  };

  const onKeyDownTreeItem = (e, item) => {
    const key = e.key;
    switch (key) {
      case 'ArrowDown':
        e.preventDefault();
        focusByDelta(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusByDelta(-1);
        break;
      case 'Home':
        e.preventDefault();
        if (firstItemId) {
          setFocusedItemId(firstItemId);
        }
        break;
      case 'End':
        e.preventDefault();
        if (lastItemId) {
          setFocusedItemId(lastItemId);
        }
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (item.type === 'building') {
          if (!isBuildingExpanded(item.building)) {
            expandBuilding(item.building);
          } else {
            focusFirstChild(item.building.id);
          }
        }
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (item.type === 'building') {
          if (isBuildingExpanded(item.building)) {
            collapseBuilding(item.building);
          }
        } else if (item.type === 'level') {
          focusParent(item);
        }
        break;
      case 'Enter':
      case ' ': // Spacebar
        e.preventDefault();
        if (item.type === 'building') {
          if (isBuildingExpanded(item.building)) {
            collapseBuilding(item.building);
          } else {
            expandBuilding(item.building);
          }
        } else if (item.type === 'level') {
          bus.send('mapLevelSelector/selectLevel', {
            id: item.level.id,
            centerMap: true,
            userInitiated: true,
          });
        }
        break;
      default:
        break;
    }
  };

  const getBuildingCounts = () => {
    const searchLocations = map(result => result.position, state.searchResults);
    const byBuilding = groupBy(result => result.buildingId);
    return byBuilding(searchLocations) || [];
  };

  const buildingCounts = getBuildingCounts();

  const getLevelCounts = buildingResults => {
    const byLevel = groupBy(result => result.floorId);
    return byLevel(buildingResults);
  };

  const handleBuildingSelectButtonClick = building => {
    bus.send('mapLevelSelector/selectBuilding', { id: building.id, userInitiated: true });
  };

  const handleLevelSelectButtonClick = level => {
    bus.send('mapLevelSelector/selectLevel', {
      id: level.id,
      centerMap: true,
      userInitiated: true,
    });
  };

  const closeMenu = () => {
    bus.send('mapLevelSelectorOnline/closeLevelSelector');
    setIsComponentFocused(false);
  };

  const renderBuildingSelectorMobile = (building, selectedBuilding, buildingResults) => {
    const levelCounts = buildingResults ? getLevelCounts(buildingResults) : [];
    const isSelected = selectedBuilding && building.id === selectedBuilding.id;
    const buildingItemId = `building-${building.id}`;
    const expanded = isSelected;
    return (
      <BuildingWrapperMobile key={building.id}>
        <BuildingDropdownMobile
          ref={el => {
            itemRefs.current[buildingItemId] = el;
          }}
          onClick={() => handleBuildingSelectButtonClick(building)}
          onKeyDown={e =>
            onKeyDownTreeItem(e, {
              type: 'building',
              id: building.id,
              building,
            })
          }
          role="treeitem"
          aria-level={1}
          aria-expanded={expanded}
          aria-selected={isSelected}
          aria-disabled={isSelected}
          tabIndex={focusedItemId === buildingItemId ? 0 : -1}
          className="mobileBuildingSelector"
          aria-label={building.name}
          data-testid={'building-list-item'}
        >
          <div>
            <Icon
              cdn
              name={expanded ? 'triangle-down' : 'triangle-right'}
              width={32}
              height={32}
              fillColor={colors.mapNavText}
            />
          </div>
          <div className="buildingName">{building.name}</div>
          {length(buildingResults) > 0 ? (
            <div className="indicator" data-testid="buildingResultsIndicator">
              {length(buildingResults)}
            </div>
          ) : (
            <div />
          )}
        </BuildingDropdownMobile>
        {expanded ? (
          <Submenu role="group" aria-label={`${building.name} levels`} data-testid="expandedLevelSelector">
            {(building.levels || []).map(level =>
              renderLevelSelectorMobile(level, levelCounts[level && level.id], building.id),
            )}
          </Submenu>
        ) : (
          <div />
        )}
      </BuildingWrapperMobile>
    );
  };

  const renderLevelSelectorMobile = (level, levelResults, parentBuildingId) => {
    if (!level) {
      return null;
    }
    const { selectedLevel } = state;
    const isSelected = selectedLevel && level.id === selectedLevel.id;
    const levelItemId = `level-${level.id}`;
    return (
      <LevelDropdownMobile
        key={level.id}
        ref={el => {
          itemRefs.current[levelItemId] = el;
        }}
        onClick={() => handleLevelSelectButtonClick(level)}
        onKeyDown={e =>
          onKeyDownTreeItem(e, {
            type: 'level',
            id: level.id,
            level,
            parentId: parentBuildingId,
          })
        }
        role="treeitem"
        aria-level={2}
        aria-selected={isSelected}
        tabIndex={focusedItemId === levelItemId ? 0 : -1}
        className={isSelected ? 'selectedBg' : null}
        data-testid="level-list-item"
        aria-label={`${level.details || level.name}${level.details && level.name ? ' ' : ''}${level.details ? level.name : ''}`}
      >
        <div className={isSelected ? 'selected' : 'notSelected'} />
        <div className="levelName">{level.details}</div>
        <div className="secondaryLabel">{level.name}</div>
        {length(levelResults) > 0 ? (
          <div className="indicator" data-testid="levelResultsIndicator">
            {length(levelResults)}
          </div>
        ) : (
          <div />
        )}
      </LevelDropdownMobile>
    );
  };

  return (
    <MapNavigatorMobileWrapper id="expandedBuildingSelector">
      <DragCloseButton
        className="exitSelector"
        onClick={closeMenu}
        type="button"
        aria-label="Collapse Map Navigator"
        title="Collapse Map Navigator"
        data-testid="DragCloseButton"
      />
      <MapNavigatorMobile
        data-testid="expandedBuildingSelector"
        role="tree"
        aria-label="Buildings and Levels"
        aria-multiselectable={false}
      >
        {map(
          building => renderBuildingSelectorMobile(building, state.selectedBuilding, buildingCounts[building.id]),
          state.buildings,
        )}
      </MapNavigatorMobile>
    </MapNavigatorMobileWrapper>
  );
};

const FocusTrappedLevelSelector = withFocusTrap(OpenedMapLevelSelectorMobile, 'expandedBuildingSelector');

const MapLevelSelectorViewMobile = props => {
  const [state, setState] = useState(props.widgetState.getState());
  useEffect(() => props.widgetState.addCallback(setState), [props.widgetState]);

  if (state.isMenuOpen) {
    return <FocusTrappedLevelSelector {...props} />;
  } else {
    return null;
  }
};

export default MapLevelSelectorViewMobile;
